/**
 * @fileoverview Session age validation and freshness utilities
 * @module lib/session-validator
 */

import type { Session } from '@supabase/supabase-js';

/**
 * Session freshness thresholds in milliseconds
 */
export const SESSION_THRESHOLDS = {
  /** Warn if session expires in < 5 minutes */
  WARNING_THRESHOLD: 5 * 60 * 1000,

  /** Consider critical if expires in < 2 minutes */
  CRITICAL_THRESHOLD: 2 * 60 * 1000,

  /** Maximum acceptable session age (50 minutes) */
  MAX_AGE: 50 * 60 * 1000,
} as const;

/**
 * Session validation result
 */
export interface SessionValidationResult {
  /** Whether session is fresh enough for use */
  isValid: boolean;

  /** Severity level of staleness */
  severity: 'fresh' | 'warning' | 'critical' | 'expired';

  /** Time until expiry in milliseconds (negative if expired) */
  timeUntilExpiry: number;

  /** Human-readable reason */
  reason: string;

  /** Whether refresh should be attempted */
  shouldRefresh: boolean;
}

/**
 * Validates session freshness for admin operations.
 *
 * Checks:
 * - Session exists
 * - Session has expiry timestamp
 * - Session hasn't expired
 * - Session isn't about to expire
 *
 * @param session - Session from AuthProvider
 * @returns Validation result with freshness status
 *
 * @example
 * ```typescript
 * const validation = validateSessionAge(session);
 * if (!validation.isValid) {
 *   console.warn('Stale session:', validation.reason);
 *   if (validation.shouldRefresh) {
 *     await refreshSession();
 *   }
 * }
 * ```
 */
export function validateSessionAge(
  session: Session | null | undefined
): SessionValidationResult {
  // No session
  if (!session) {
    return {
      isValid: false,
      severity: 'expired',
      timeUntilExpiry: -1,
      reason: 'No session available',
      shouldRefresh: true,
    };
  }

  // Missing expiry timestamp
  if (!session.expires_at) {

    // Fallback: calculate from expires_in
    if (session.expires_in) {
      const estimatedExpiry = Date.now() + (session.expires_in * 1000);
      const timeUntilExpiry = estimatedExpiry - Date.now();

      return analyzeTimeUntilExpiry(timeUntilExpiry);
    }

    // No expiry info at all - assume valid but warn
    return {
      isValid: true,
      severity: 'warning',
      timeUntilExpiry: 0,
      reason: 'Session missing expiry information',
      shouldRefresh: false,
    };
  }

  // Calculate time until expiry
  const expiryMs = session.expires_at * 1000; // Convert to milliseconds
  const now = Date.now();
  const timeUntilExpiry = expiryMs - now;

  return analyzeTimeUntilExpiry(timeUntilExpiry);
}

/**
 * Analyzes time until expiry and returns validation result.
 */
function analyzeTimeUntilExpiry(timeUntilExpiry: number): SessionValidationResult {
  // Already expired
  if (timeUntilExpiry < 0) {
    return {
      isValid: false,
      severity: 'expired',
      timeUntilExpiry,
      reason: `Session expired ${formatDuration(Math.abs(timeUntilExpiry))} ago`,
      shouldRefresh: true,
    };
  }

  // Critical - expires very soon
  if (timeUntilExpiry < SESSION_THRESHOLDS.CRITICAL_THRESHOLD) {
    return {
      isValid: false,
      severity: 'critical',
      timeUntilExpiry,
      reason: `Session expires in ${formatDuration(timeUntilExpiry)}`,
      shouldRefresh: true,
    };
  }

  // Warning - expires soon
  if (timeUntilExpiry < SESSION_THRESHOLDS.WARNING_THRESHOLD) {
    return {
      isValid: true, // Still usable, but warn
      severity: 'warning',
      timeUntilExpiry,
      reason: `Session expires in ${formatDuration(timeUntilExpiry)}`,
      shouldRefresh: true,
    };
  }

  // Fresh session
  return {
    isValid: true,
    severity: 'fresh',
    timeUntilExpiry,
    reason: `Session valid for ${formatDuration(timeUntilExpiry)}`,
    shouldRefresh: false,
  };
}

/**
 * Formats duration in human-readable format.
 */
function formatDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }
  return `${seconds}s`;
}

/**
 * Gets session age in milliseconds.
 *
 * @param session - Session to check
 * @returns Age in milliseconds, or null if cannot determine
 */
export function getSessionAge(session: Session | null | undefined): number | null {
  if (!session?.expires_at || !session?.expires_in) {
    return null;
  }

  // Session was issued at: expires_at - expires_in
  const issuedAt = (session.expires_at - session.expires_in) * 1000;
  const now = Date.now();

  return now - issuedAt;
}

/**
 * Checks if session is fresh enough for critical operations.
 *
 * @param session - Session to check
 * @param maxAge - Maximum acceptable age in milliseconds
 * @returns True if session is fresh enough
 */
export function isSessionFresh(
  session: Session | null | undefined,
  maxAge: number = SESSION_THRESHOLDS.MAX_AGE
): boolean {
  const age = getSessionAge(session);

  if (age === null) {
    // Cannot determine age - assume stale for safety
    return false;
  }

  return age < maxAge;
}

/**
 * Track session age metrics for monitoring.
 *
 * Logs session validation status and can be extended to send
 * metrics to analytics or error tracking services.
 *
 * @param session - Session to track
 */
export function trackSessionMetrics(session: Session | null): void {
  const validation = validateSessionAge(session);

  // Log metrics

  // Send to error tracking if critical
  if (validation.severity === 'critical' || validation.severity === 'expired') {

    // TODO: Send to Sentry/error tracking service
    // if (typeof window !== 'undefined' && (window as any).Sentry) {
    //   (window as any).Sentry.captureMessage('Session staleness detected', {
    //     level: validation.severity === 'expired' ? 'error' : 'warning',
    //     extra: { validation, age },
    //   });
    // }
  }
}
